/**
 * Structured Data (JSON-LD) utilities for SEO
 * Schema.org types for Google Rich Results
 */

interface Artwork {
  id: string;
  slug?: string;
  title: string;
  description?: string;
  price: number;
  image_url?: string;
  artist_id?: string;
  artist_name?: string;
  created_at?: string;
}

interface Artist {
  id: string;
  slug?: string;
  username?: string;
  full_name?: string;
  bio?: string;
  avatar_url?: string;
}

/**
 * Organization - Company/Brand information
 */
export const getOrganizationData = () => {
  const baseUrl = window.location.origin;
  
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Nordic Art Collective",
    "alternateName": "Andberg Group AB",
    "url": baseUrl,
    "logo": `${baseUrl}/logo.svg`,
    "description": "Nordens ledande marknadsplats för konst. Köp originalverk, prints och konstmaterial direkt från konstnärer.",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "support@nordicartcollective.se",
      "contactType": "Customer Service",
      "areaServed": "SE",
      "availableLanguage": ["Swedish", "English"]
    },
    "sameAs": [
      "https://www.instagram.com/nordicartcollective",
      "https://www.facebook.com/nordicartcollective"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SE"
    }
  };
};

/**
 * WebSite - Main website with search functionality
 */
export const getWebSiteData = () => {
  const baseUrl = window.location.origin;
  
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Nordic Art Collective",
    "url": baseUrl,
    "description": "Upptäck unik konst från nordiska konstnärer. Köp original, prints och konstmaterial.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/explore?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

/**
 * Product - Individual artwork
 */
export const getProductData = (artwork: Artwork) => {
  const baseUrl = window.location.origin;
  const imageUrl = artwork.image_url?.startsWith('http') 
    ? artwork.image_url 
    : `${baseUrl}${artwork.image_url || '/logo.svg'}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": artwork.title,
    "description": artwork.description || `Konstverk av ${artwork.artist_name || 'konstnär'}`,
    "image": imageUrl,
    "brand": {
      "@type": "Brand",
      "name": artwork.artist_name || "Nordic Art Collective Konstnär"
    },
    "offers": {
      "@type": "Offer",
      "url": `${baseUrl}/artwork/${artwork.slug || artwork.id}`,
      "priceCurrency": "SEK",
      "price": artwork.price,
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "Nordic Art Collective"
      }
    },
    "category": "Art",
    "audience": {
      "@type": "PeopleAudience",
      "audienceType": "Art Collectors and Enthusiasts"
    }
  };
};

/**
 * Person - Artist profile
 */
export const getPersonData = (artist: Artist) => {
  const baseUrl = window.location.origin;
  const avatarUrl = artist.avatar_url?.startsWith('http')
    ? artist.avatar_url
    : artist.avatar_url ? `${baseUrl}${artist.avatar_url}` : `${baseUrl}/logo.svg`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": artist.full_name || artist.username || "Nordic Art Collective Konstnär",
    "url": `${baseUrl}/artist/${artist.slug || artist.id}`,
    "image": avatarUrl,
    "description": artist.bio || `Konstnär på Nordic Art Collective - ${artist.full_name || artist.username}`,
    "jobTitle": "Artist",
    "worksFor": {
      "@type": "Organization",
      "name": "Nordic Art Collective"
    }
  };
};

/**
 * ItemList - Collection of artworks
 */
export const getItemListData = (artworks: Artwork[], listName: string = "Konstverk") => {
  const baseUrl = window.location.origin;
  
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName,
    "numberOfItems": artworks.length,
    "itemListElement": artworks.slice(0, 10).map((artwork, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": artwork.title,
        "url": `${baseUrl}/artwork/${artwork.slug || artwork.id}`,
        "image": artwork.image_url?.startsWith('http') 
          ? artwork.image_url 
          : `${baseUrl}${artwork.image_url || '/logo.svg'}`,
        "offers": {
          "@type": "Offer",
          "price": artwork.price,
          "priceCurrency": "SEK"
        }
      }
    }))
  };
};

/**
 * BreadcrumbList - Navigation breadcrumbs
 */
export const getBreadcrumbData = (breadcrumbs: { name: string; url: string }[]) => {
  const baseUrl = window.location.origin;
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${baseUrl}${crumb.url}`
    }))
  };
};

/**
 * FAQPage - Frequently Asked Questions
 */
export const getFAQData = (faqs: { question: string; answer: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * CollectionPage - For categories/collections
 */
export const getCollectionPageData = (collectionName: string, description: string) => {
  const baseUrl = window.location.origin;
  
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": collectionName,
    "description": description,
    "url": window.location.href,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Nordic Art Collective",
      "url": baseUrl
    }
  };
};

/**
 * Combine multiple structured data objects
 */
export const combineStructuredData = (...dataObjects: object[]) => {
  return {
    "@context": "https://schema.org",
    "@graph": dataObjects
  };
};